import { useMemo } from 'react';
import { useAngularContext } from 'AngularContext';
import { type CurrentUser } from 'Users';
import { type NavigationSectionConfig, NavigationSectionKey } from 'NavigationHelpers';
import { getAvailableNavigationSections } from 'NavigationSections';

type UseNavigationSectionsOptions = {
    sectionsToHide?: NavigationSectionKey[];
};

type UseNavigationSectionsResult = {
    availableSections: NavigationSectionConfig[];
    allSections: NavigationSectionConfig[];
};

const DEFAULT_HIDDEN_SECTIONS_FOR_DESKTOP = [
    NavigationSectionKey.switch_program, // switch_program option is only needed on mobile
    NavigationSectionKey.application_status, // application_status should only be accessible via settings_dir
    NavigationSectionKey.application, // unique styling, so omit from ng-repeat
    NavigationSectionKey.support, // unique styling, so omit from ng-repeat
    NavigationSectionKey.sign_out, // unique styling, so omit from ng-repeat
];

export function useNavigationSections(
    currentUser: CurrentUser | null,
    options: UseNavigationSectionsOptions = {},
): UseNavigationSectionsResult {
    // useAngularContext returns the $injector directly (it IS the IInjectorService)
    const $injector = useAngularContext();
    const { sectionsToHide = DEFAULT_HIDDEN_SECTIONS_FOR_DESKTOP } = options;

    const [availableSections, allSections] = useMemo(() => {
        if (!currentUser || !$injector) {
            return [[], []];
        }

        return getAvailableNavigationSections($injector, currentUser, sectionsToHide);
    }, [currentUser, $injector, sectionsToHide]);

    return { availableSections, allSections };
}
